import React, { useState } from 'react';

const VirtualCFOServices = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: 'I need help with Virtual CFO Services.',
    website_hp: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  
  try {
    const response = await fetch("https://akiradvisory.in/send_mail.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });

    if (response.ok) {
      setFormStatus('success');
      // Reset form while keeping the default message
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: 'I need help with Virtual CFO Services.',
        website_hp: ''
      });
    } else {
      setFormStatus('error');
    }
  } catch (error) {
    setFormStatus('error');
  } finally {
    setIsSubmitting(false);
    setTimeout(() => setFormStatus(''), 5000);
  }
};

  return (
    <main>
      {/* Hero Section */}
      <section className="relative bg-blue-700 pt-16 pb-12">
        <div className="absolute inset-0 opacity-10 bg-cover bg-center"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:w-2/3">
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Virtual CFO Services
            </h1>

            <p className="mt-4 text-xl text-blue-100">
              Professional Virtual CFO services providing strategic financial leadership, financial planning, and comprehensive financial management for businesses. Get expert CFO guidance without the cost of a full-time executive.
            </p>
            <div className="mt-8">
              <a href="#contact-section" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-700 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
                  <span className="mr-2">📈</span> Get Strategic Financial Leadership
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Navigation */}
      <section className="bg-gray-50 border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ul className="flex space-x-6 overflow-x-auto text-sm font-medium">
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#details">Overview & Benefits</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#procedure">Our Process</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#documents">What We Need</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#you-get">What You Get</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#service-faqs">FAQs</a></li>
            </ul>
        </div>
      </section>

      {/* Overview Section */}
      <section id="details" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 border-b pb-2">
                Overview of Virtual CFO Services
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                A Virtual CFO (Chief Financial Officer) provides strategic financial leadership and management services on a part-time, flexible basis. This allows businesses to access expert financial guidance without the cost of a full-time executive.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our Virtual CFO services cover financial strategy, cash flow management, financial reporting, fundraising support, and business growth planning. We become your strategic financial partner, helping you make informed decisions and drive business growth.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Whether you're a startup seeking funding, a growing business managing expansion, or an established company optimizing operations, our Virtual CFOs provide the financial leadership you need.
              </p>

              <div className="mt-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4">
                  Key Features & Benefits
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Strategic Financial Planning:</strong> Develop comprehensive financial strategies and growth roadmaps aligned with business objectives.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Cash Flow Management:</strong> Proactive cash flow forecasting, working capital optimization, and liquidity management.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Financial Reporting & Analysis:</strong> Monthly management reports, KPI tracking, and performance analysis.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Fundraising Support:</strong> Investor pitch preparation, financial modeling, due diligence, and fundraising strategy.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Cost Optimization:</strong> Identify cost-saving opportunities and improve operational efficiency.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Risk Management:</strong> Financial risk assessment, internal controls, and compliance oversight.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Cost-Effective Expertise:</strong> Executive-level financial guidance at a fraction of full-time CFO cost.
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-1" id="contact-section">
              <div className="bg-gray-50 p-6 rounded-xl shadow-xl border-t-4 border-blue-600 sticky top-28">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  Quick Enquiry
                </h3>
                
                {formStatus === 'success' && (
                  <div className="p-4 bg-green-100 text-green-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">✅</span>
                    Thank you! Your enquiry has been submitted. We'll contact you shortly.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="p-4 bg-red-100 text-red-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">❌</span>
                    There was an error submitting your form. Please try again.
                  </div>
                )}

                <form className="space-y-4" onSubmit={handleFormSubmit}>
                  <input type="text" name="website_hp" value={formData.website_hp} onChange={handleInputChange} style={{ display: 'none' }}/>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Full Name" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <textarea 
                    rows="3" 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="I need help with Virtual CFO Services." 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required
                  ></textarea>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3 inline-block"></div>
                        Submitting...
                      </>
                    ) : (
                      'Request an Initial Consultation'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Procedure Section */}
      <section id="procedure" className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Our Comprehensive Process
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              8 Steps for Virtual CFO Engagement
            </p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 grid-flow-row-dense">
            {[
              { number: "01", icon: "🔍", title: "Business Assessment", desc: "Comprehensive analysis of your business, financial position, goals, and challenges." },
              { number: "02", icon: "📋", title: "Financial Health Check", desc: "Review current financial statements, cash flow, and operational efficiency." },
              { number: "03", icon: "🎯", title: "Strategy Development", desc: "Create customized financial strategy and growth roadmap for your business." },
              { number: "04", icon: "📊", title: "KPI Framework Setup", desc: "Establish key performance indicators and financial reporting dashboard." },
              { number: "05", icon: "💰", title: "Cash Flow Management", desc: "Implement cash flow forecasting and working capital optimization strategies." },
              { number: "06", icon: "📈", title: "Growth Planning", desc: "Develop financial models for expansion, fundraising, and strategic initiatives." },
              { number: "07", icon: "👥", title: "Team Guidance", desc: "Mentor your finance team and establish financial processes and controls." },
              { number: "08", icon: "🏆", title: "Ongoing Partnership", desc: "Continuous strategic guidance, performance monitoring, and growth optimization." }
            ].map((step, index) => (
              <div key={index} className="sm:col-span-2 lg:col-span-2 flex flex-col justify-between p-3 bg-white rounded-xl shadow-md text-center hover:shadow-lg transition-shadow border-t-4 border-blue-500">
                <div className="flex flex-col flex-grow justify-center">
                  <span className="text-xl font-extrabold text-blue-600 block leading-tight">{step.number}</span>
                  <span className="text-2xl mx-auto block leading-none">{step.icon}</span>
                  <h3 className="text-base font-semibold text-gray-900 leading-snug mt-0.5">{step.title}</h3>
                  <p className="text-gray-600 text-sm text-justify mt-1 leading-snug">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section id="documents" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Information We Need
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Essential Business Information
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: "📄", title: "Financial Statements", desc: "Previous 2-3 years financial statements, tax returns, and current management accounts." },
              { icon: "💰", title: "Cash Flow Details", desc: "Bank statements, accounts receivable/payable aging, and cash flow projections." },
              { icon: "🎯", title: "Business Plan", desc: "Current business plan, growth objectives, and strategic initiatives." },
              { icon: "👥", title: "Organizational Structure", desc: "Team structure, key personnel, and decision-making processes." },
              { icon: "📊", title: "Operational Data", desc: "Sales data, customer information, product/service details, and market analysis." },
              { icon: "🛡️", title: "Compliance Records", desc: "Tax compliance records, regulatory filings, and legal documentation." },
              { icon: "📈", title: "Funding Requirements", desc: "Current funding needs, investment plans, and capital expenditure requirements." },
              { icon: "⚙️", title: "Systems & Processes", desc: "Current accounting software, financial systems, and operational processes." },
              { icon: "🏆", title: "Performance Metrics", desc: "Current KPIs, performance benchmarks, and industry comparisons." }
            ].map((doc, index) => (
              <div key={index} className="p-6 border rounded-xl shadow-md flex items-start space-x-4 h-full">
                <span className="text-2xl flex-shrink-0 mt-1">{doc.icon}</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{doc.title}</h3>
                  <p className="text-gray-600 text-sm mt-1 text-justify">{doc.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section id="you-get" className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Your Deliverables
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              What Akir Advisory Delivers
            </p>
          </div>

          {/* 5 equal boxes in a single row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { icon: "📊", title: "Financial Dashboards", desc: "Real-time financial dashboards with key performance indicators and business metrics." },
              { icon: "📋", title: "Strategic Reports", desc: "Monthly management reports with insights, analysis, and actionable recommendations." },
              { icon: "📈", title: "Growth Strategy", desc: "Comprehensive growth roadmap with financial modeling and expansion scenarios." },
              { icon: "💰", title: "Cash Flow Models", desc: "Detailed cash flow projections and working capital optimization strategies." },
              { icon: "🎧", title: "Ongoing Strategic Support", desc: "Regular strategy sessions, financial decision support, fundraising assistance, team mentoring" }
            ].map((item, index) => (
              <div key={index} className="p-5 bg-white rounded-xl shadow-lg border-l-4 border-green-500 flex flex-col h-full">
                <span className="text-2xl mb-2 block">{item.icon}</span>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-snug">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section id="service-faqs" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                  <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
                      Common Questions
                  </h2>
                  <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                      Virtual CFO Services FAQs
                  </p>
              </div>
              
              <div className="space-y-4 max-w-4xl mx-auto">
                {[
                  { 
                    question: "How is a Virtual CFO different from a traditional CFO?", 
                    answer: "A Virtual CFO provides the same strategic financial leadership but works remotely on a flexible, part-time basis, making executive expertise accessible and affordable for growing businesses without the cost of a full-time executive." 
                  },
                  { 
                    question: "What size business benefits from Virtual CFO services?", 
                    answer: "Businesses with revenues from ₹50 lakh to ₹50 crore typically benefit most. Startups seeking funding, scaling companies managing growth, and established SMEs optimizing operations all gain from strategic financial guidance." 
                  },
                  { 
                    question: "How quickly can we see results from Virtual CFO services?", 
                    answer: "Most clients see immediate improvements in financial clarity within 30 days, with strategic impact on cash flow and decision-making within 3-6 months. Full strategic transformation typically occurs over 6-12 months." 
                  },
                  { 
                    question: "Can a Virtual CFO help with fundraising?", 
                    answer: "Absolutely. We prepare financial models, investor presentations, due diligence materials, help negotiate terms with investors, and provide ongoing investor relations support." 
                  },
                  { 
                    question: "What's the typical engagement duration?", 
                    answer: "Most clients engage for 6-12 months minimum to see strategic impact, though we offer flexible month-to-month arrangements. The engagement can be scaled up or down based on your business needs." 
                  },
                  { 
                    question: "Do we need to have accounting software in place?", 
                    answer: "While helpful, it's not required initially. We can help you implement the right accounting systems and financial processes as part of our engagement package." 
                  }
                ].map((faq, index) => (
                  <div key={index} className="border rounded-lg p-5 shadow-sm">
                    <h4 className="font-semibold text-lg text-gray-900 mb-2">{faq.question}</h4>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                ))}
              </div>
          </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-blue-700/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-white">
                Ready for Strategic Financial Leadership?
            </h2>
            <p className="mt-4 text-xl text-blue-100 max-w-3xl mx-auto">
                Let our Virtual CFO experts provide the financial strategy and guidance your business needs to grow and succeed.
            </p>
            <a href="#contact-section" className="mt-8 inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-700 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
                <span className="mr-2">📞</span> Consult Our Expert Today
            </a>
        </div>
      </section>
    </main>
  );
};

export default VirtualCFOServices;